import { AuthPageLayout } from "Components/Auth/Shared/AuthPageLayout";
import { type FormData, SignInForm } from "Components/Auth/SignIn/SignInForm";
import { useNavigate } from "Navigation/useNavigate";
import { resetError, setSignInStep } from "State/Auth/AuthReducer";
import { SignInStep } from "State/Auth/Models/AuthStateModels";
import { signInAsync } from "State/Auth/SignIn/SignInState";
import type { RootStateType } from "State/Store";
import { Resources, useResource, useServerError } from "Translations/Resources";
import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";
import { removeLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

export const SignInPage = () => {
  const [keyToResetForm, toggleKeyToResetForm] = useState(true);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useResource();

  useEffect(() => {
    dispatch(resetError());
    // A sign-in is never a role registration — drop any intent left by an abandoned
    // /sign-up-hunter or roleIntent link. Without this, a first-time SSO login from this
    // page (which implicitly creates the account) would inherit a role intent and skip
    // the immediate Customer referral in favor of a deferred one.
    removeLocalStorageItem(LocalStorageKeys.RoleIntent);
  }, [dispatch]);

  const { translateError } = useServerError(
    Resources.SignIn.Error,
    Resources.SignIn.Error.General,
  );

  const {
    isLoading,
    errorCode,
    signInStep: step,
  } = useSelector((e: RootStateType) => e.auth);

  const login = new URLSearchParams(location.search).get("login") || "";

  const onSubmit = ({ login, password }: FormData) => {
    dispatch(
      signInAsync.request({
        model: { login, password },
        navigate,
      }),
    );
  };
  const onFormReset = () => {
    toggleKeyToResetForm(!keyToResetForm);
    dispatch(setSignInStep(SignInStep.Credentials));
  };

  return (
    <AuthPageLayout
      title={t(Resources.SignIn.Title)}
      isLoading={isLoading}
      pageType="sign-in"
      key={`${keyToResetForm}`}
    >
      <SignInForm
        step={step}
        isLoading={isLoading}
        onSubmit={onSubmit}
        serverError={translateError(errorCode)}
        onReset={onFormReset}
        login={login}
      />
    </AuthPageLayout>
  );
};

export default SignInPage;
